// src/lib/api.js
import axios from "axios";

const api = axios.create({
  baseURL: "/api",
  withCredentials: true,
  xsrfCookieName: 'csrftoken', 
  xsrfHeaderName: 'X-CSRFToken',
});

function getStoredToken(key) {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key) ?? null;
}

function setStoredToken(key, value) {
  if (localStorage.getItem("refresh")) {
    localStorage.setItem(key, value);
  } else {
    sessionStorage.setItem(key, value);
  }
}

api.interceptors.request.use((config) => {
  const t = getStoredToken("access");
  if (t) config.headers.Authorization = `Bearer ${t}`;

  // GET は毎回キャッシュを回避する。
  // Apache の mod_expires が API 応答に max-age=30日 を付けていたため、修正前に
  // ブラウザへ保存された応答がそのまま再利用され、参加者管理などが古いデータを
  // 表示し続ける。Apache 側は no-store に修正済みだが、既存のキャッシュ項目は
  // 消えないので、URL を毎回変えて確実に取り直す。
  if ((config.method || "get").toLowerCase() === "get") {
    const params = config.params;
    const isPlainObject =
      params == null || Object.prototype.toString.call(params) === "[object Object]";
    if (isPlainObject) {
      config.params = { ...(params || {}), _t: Date.now() };
    }
    config.headers["Cache-Control"] = "no-cache";
    config.headers["Pragma"] = "no-cache";
  }

  return config;
});

let refreshing = false;
let waiters = [];

async function refreshAccessToken() {
  const refresh = getStoredToken("refresh");
  if (!refresh) throw new Error("NO_REFRESH");
  

  const res = await axios.post("/api/auth/token/refresh/", { refresh });
  const { access } = res.data || {};
  if (!access) throw new Error("REFRESH_FAILED");
  setStoredToken("access", access);
  return access;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { config, response } = error || {};
    
    if (!response) {
      return Promise.reject(error);
    }

    const urlString = String(config.url).toLowerCase();

    // リフレッシュ/トークン発行自体の失敗は認証切れ → ログインへ
    if (urlString.includes('refresh') || urlString.includes('token')) {
      clearAllTokens();
      redirectToLogin();
      return Promise.reject(error);
    }

    // 401 以外（403=権限不足 / 404 / 500 等）は「認証切れ」ではない。
    // トークンを消さず・リダイレクトせず、そのままエラーを返して各画面で処理させる。
    // ※ 以前はここで全エラーをログアウト扱いし、権限不足の勤務地マスタ等で /m/login へ飛んでいた。
    if (response.status !== 401) {
      return Promise.reject(error);
    }

    // 401 を一度リトライ済みなら、本当に認証切れとしてログインへ。
    // 判定は「リクエスト単位」のフラグで行う。URL 単位のグローバル Set にすると、
    // 同じ URL（例: 申請ステータス変更 API）を後日また呼んだときに
    // リフレッシュを試さずログアウトしてしまう（BUG-202）。
    if (config.__retried) {
      clearAllTokens();
      redirectToLogin();
      return Promise.reject(error);
    }

    try {
      if (refreshing) {
        await new Promise((r) => waiters.push(r));
      } else {
        refreshing = true;
        await refreshAccessToken();
        refreshing = false;
        waiters.forEach((r) => r());
        waiters = [];
      }

      config.__retried = true;
      const t = getStoredToken("access");
      if (t) {
        config.headers.Authorization = `Bearer ${t}`;
      }

      return api.request(config);

    } catch (e) {
      refreshing = false;
      waiters.forEach((r) => r());
      waiters = [];

      clearAllTokens();
      redirectToLogin();
      return Promise.reject(error);
    }
  }
);

function clearAllTokens() {
  ["access", "refresh"].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
}

function redirectToLogin() {
  if (window.location.pathname.startsWith('/admin') || window.location.pathname.startsWith('/dashboard')) {
    window.location.href = '/admin/login';
  } else {
    window.location.href = '/m/login';
  }
}

export default api;